import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { RefAttributes } from 'react';

import styles from './styles.module.scss';
import type { Variant, InferPropTypes } from '../../../index.ts';
import addVariants from '../../../utils/addVariants/index.ts';

export const ALIGN_TYPES = ['left', 'center', 'right'] as const;
export const VARIANTS = [
  'default',
  'no-padding',
  'no-wrap',
  'comfortable',
  'bold',
  'title'
] as const;
const propTypes = {
  /**
   * The list item second line of content
   */
  secondary: PropTypes.node,
  /**
   * The list item text content
   */
  children: PropTypes.node,
  /**
   * Decides the variant of the list item
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * Additional classnames to apply
   */
  className: PropTypes.string,
  /**
   * Align text in a direction
   */
  align: PropTypes.oneOf(ALIGN_TYPES)
};
const defaultProps = {
  className: '',
  children: null,
  variant: 'default',
  secondary: false,
  align: 'left'
};
export type ListItemTextProps = InferPropTypes<
  typeof propTypes,
  typeof defaultProps,
  {
    secondary?: React.ReactNode;
    children?: React.ReactNode;
    variant?: Variant<(typeof VARIANTS)[number]>;
    align?: (typeof ALIGN_TYPES)[number];
  }
> &
  RefAttributes<HTMLSpanElement>;

const ListItemText = ({
  children,
  secondary,
  align,
  variant: variants,
  className,
  ...rest
}: ListItemTextProps): JSX.Element => {
  const mainText = (
    <span
      className={classnames(
        addVariants('aglmd-list-item__text', styles, `align-${align}`, variants),
        className
      )}
      {...rest}
    >
      {children}
      {secondary && (
        <span
          className={classnames(
            addVariants('aglmd-list-item__text-secondary', styles, `align-${align}`, variants),
            className
          )}
          {...rest}
        >
          {secondary}
        </span>
      )}
    </span>
  );

  return mainText;
};

ListItemText.propTypes = propTypes;
ListItemText.defaultProps = defaultProps;

export default ListItemText;
